(() => {
  const SEARCH_SELECTOR = '[data-cd13-search]';
  const INPUT_SELECTOR = '[data-cd13-search-input]';
  const RESULTS_SELECTOR = '[data-cd13-search-results]';
  const MENU_DATA_SELECTOR = '[data-cd13-menu-data]';
  const PRODUCT_DATA_SELECTOR = '[data-cd13-product-reference-data]';
  const MIN_QUERY_LENGTH = 2;
  const DEBOUNCE_MS = 180;
  const VAT_MULTIPLIER = 1.2;
  const referenceCache = new Map();

  const normalize = (value) => String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

  // Same "free text" tokenisation as the profiles collection finder: strip a
  // "prof"/"profil"/"profile" prefix glued to a number ("prof45" -> "45") and
  // ignore filler words, so global search understands the same shorthand.
  const IGNORED_SEARCH_WORDS = new Set(['prof','profs','profil','profils','profile','profiles','barre','barres','alu','aluminium','en','de','du','des','le','la','les','sur','mesure']);

  const searchTokens = (value) => normalize(value)
    .replace(/[^a-z0-9]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((token) => token.replace(/^(?:prof|profil|profile)(?=\d)/, ''))
    .filter(Boolean)
    .filter((token) => !IGNORED_SEARCH_WORDS.has(token));

  const buildEffectiveQuery = (query) => {
    const tokens = searchTokens(query);
    return tokens.length ? tokens.join(' ') : query;
  };

  const titleTokenScore = (item, tokens) => {
    if (!tokens.length) return 0;
    const haystack = normalize(item?.title || '');
    const matched = tokens.filter((token) => haystack.includes(token)).length;
    if (!matched) return 0;
    return matched === tokens.length ? 50 : 20;
  };

  const escapeHTML = (value) => String(value || '').replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  }[character]));

  const getRoot = () => window.Shopify?.routes?.root || '/';
  const getItemUrl = (item) => item?.url || item?.online_store_url || '#';
  const getImageUrl = (item) => typeof item?.image === 'string'
    ? item.image
    : item?.image?.url || item?.featured_image?.url || item?.featured_image?.src || '';

  // IMPORTANT: Shopify's predictive search (`search/suggest.json`) returns
  // product prices already in the shop's full currency unit (e.g. 45.0),
  // unlike the cart/product Ajax APIs which use minor units (cents). We
  // normalise everything to cents here so downstream formatMoney (ours and
  // CD13QuickAdd's, which both divide by 100) stays consistent everywhere.
  const parsePriceToCents = (item) => {
    const raw = item?.price_min ?? item?.price_max ?? item?.price ?? 0;
    const numeric = typeof raw === 'number'
      ? raw
      : Number(String(raw).replace(/[^0-9.,]/g, '').replace(',', '.'));
    return Number.isFinite(numeric) ? Math.round(numeric * 100) : 0;
  };

  const formatMoney = (value, currency) => window.CD13QuickAdd?.formatMoney
    ? window.CD13QuickAdd.formatMoney(value, currency)
    : new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
        style: 'currency',
        currency
      }).format((Number(value) || 0) / 100);

  const parseMenuData = (form) => {
    const script = form.querySelector(MENU_DATA_SELECTOR);
    if (!script) return [];

    try {
      const data = JSON.parse(script.textContent || '[]');
      return Array.isArray(data) ? data.filter((item) => item?.title && item?.url) : [];
    } catch (_error) {
      return [];
    }
  };

  const menuMatches = (menuData, query) => menuData.filter((item) => {
    const haystack = normalize([item.title, item.parent].filter(Boolean).join(' '));
    return normalize(query).split(/\s+/).every((term) => haystack.includes(term));
  }).slice(0, 6);

  const isReferenceQuery = (query) => /^[a-z0-9-]{6,}$/i.test(String(query || '').trim());

  const variantValues = (variant) => [
    variant?.supplierReference,
    variant?.nationalReference,
    variant?.sku
  ].filter(Boolean);

  const variantMatchScore = (variant, query) => {
    const normalizedQuery = normalize(query);
    let score = 0;

    variantValues(variant).forEach((value) => {
      const normalizedValue = normalize(value);
      if (normalizedValue === normalizedQuery) score = Math.max(score, 100);
      else if (normalizedValue.startsWith(normalizedQuery)) score = Math.max(score, 60);
      else if (normalizedValue.includes(normalizedQuery)) score = Math.max(score, 30);
    });

    return score;
  };

  const selectVariant = (variants, query) => {
    if (!Array.isArray(variants) || !variants.length) return null;

    const ranked = variants
      .map((variant, index) => ({ variant, index, score: variantMatchScore(variant, query) }))
      .sort((left, right) => right.score - left.score || left.index - right.index);

    return ranked[0]?.variant || null;
  };

  const productMatchScore = (item, query) => {
    const variantScore = Math.max(
      0,
      ...(item._referenceData?.variants || []).map((variant) => variantMatchScore(variant, query))
    );
    const titleScore = titleTokenScore(item, searchTokens(query));
    return Math.max(variantScore, titleScore);
  };

  const fetchReferenceData = async (item, signal) => {
    const itemUrl = getItemUrl(item);
    if (!itemUrl || itemUrl === '#') return { variants: [] };

    const productUrl = new URL(itemUrl, window.location.origin);
    productUrl.search = '';
    productUrl.hash = '';
    const cacheKey = productUrl.pathname;

    if (referenceCache.has(cacheKey)) return referenceCache.get(cacheKey);

    const requestUrl = new URL(productUrl.toString());
    requestUrl.searchParams.set('section_id', 'cd13-search-product-data');

    const request = fetch(requestUrl.toString(), {
      headers: { Accept: 'text/html' },
      signal
    }).then(async (response) => {
      if (!response.ok) return { variants: [] };

      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const script = documentFragment.querySelector(PRODUCT_DATA_SELECTOR);
      if (!script) return { variants: [] };

      try {
        const data = JSON.parse(script.textContent || '{}');
        return {
          variants: Array.isArray(data.variants) ? data.variants : []
        };
      } catch (_error) {
        return { variants: [] };
      }
    });

    // Fix: a request cancelled by a later keystroke (AbortError) — or any other
    // network failure — must NOT stay cached forever. Previously the cached
    // promise stayed rejected permanently, so any future search that matched
    // the same product would immediately reject the whole Promise.all in
    // enrichProducts() and silently skip rendering (see below).
    referenceCache.set(cacheKey, request);
    request.catch(() => {
      referenceCache.delete(cacheKey);
    });

    return request;
  };

  const enrichProducts = async (products, signal) => Promise.all(products.map(async (item) => {
    // Fix: isolate failures per product. A single aborted/failed reference
    // fetch must not reject the entire Promise.all — that used to prevent
    // show() from ever being called, so the whole dropdown stayed empty.
    let referenceData = { variants: [] };
    try {
      referenceData = await fetchReferenceData(item, signal);
    } catch (_error) {
      // Ignore — the product still renders, just without enriched reference data.
    }
    return { ...item, _referenceData: referenceData };
  }));

  const prepareProducts = (products, query) => {
    const ranked = [...products].sort((left, right) => {
      const scoreDifference = productMatchScore(right, query) - productMatchScore(left, query);
      return scoreDifference || 0;
    });

    if (!isReferenceQuery(query)) return ranked;

    const authoritativeMatches = ranked.filter((item) => productMatchScore(item, query) > 0);
    return authoritativeMatches.length ? authoritativeMatches : ranked;
  };

  const renderIcon = (type) => ({
    query: '⌕',
    menu: '↳',
    collection: '▦',
    page: '□',
    article: '✎'
  }[type] || '•');

  const renderGenericItem = (item, type) => {
    const meta = escapeHTML(item.parent || item.blog_title || item.type || '');
    return `<a class="cd13-search-result cd13-search-result--${type}" href="${escapeHTML(getItemUrl(item))}" role="option">
      <span class="cd13-search-result__icon" aria-hidden="true">${renderIcon(type)}</span>
      <span class="cd13-search-result__body">
        <span class="cd13-search-result__title">${escapeHTML(item.title || item.text || item.query || item.name || '')}</span>
        ${meta ? `<span class="cd13-search-result__meta">${meta}</span>` : ''}
      </span>
    </a>`;
  };

  const renderProductItem = (item, form, query) => {
    const itemUrl = getItemUrl(item);
    const itemImage = getImageUrl(item);
    const price = parsePriceToCents(item);
    const currency = form.dataset.currency || window.Shopify?.currency?.active || 'EUR';
    const vendor = item.vendor || item.product_type || '';
    const variants = item._referenceData?.variants || [];
    const selectedVariant = selectVariant(variants, query);
    const supplierReference = selectedVariant?.supplierReference || '';
    const destinationUrl = new URL(itemUrl, window.location.origin);

    if (selectedVariant?.id && variantMatchScore(selectedVariant, query) > 0) {
      destinationUrl.searchParams.set('variant', selectedVariant.id);
    }

    const productLink = `${destinationUrl.pathname}${destinationUrl.search}${destinationUrl.hash}`;
    const meta = escapeHTML([
      vendor,
      supplierReference ? `Réf. fournisseur ${supplierReference}` : ''
    ].filter(Boolean).join(' · '));
    const loadingLabel = escapeHTML(form.dataset.labelLoading || 'Vérification…');

    return `<article class="cd13-search-product-card cd13-quick-product" data-cd13-quick-product data-product-url="${escapeHTML(productLink)}">
      <a class="cd13-search-result cd13-search-result--product" href="${escapeHTML(productLink)}" role="option">
        ${itemImage
          ? `<img class="cd13-search-result__image" src="${escapeHTML(itemImage)}" alt="" loading="lazy">`
          : '<span class="cd13-search-result__image cd13-search-result__image--empty" aria-hidden="true"></span>'}
        <span class="cd13-search-result__body">
          <span class="cd13-search-result__title">${escapeHTML(item.title || '')}</span>
          ${meta ? `<span class="cd13-search-result__meta">${meta}</span>` : ''}
        </span>
        <span class="cd13-search-result__prices">
          <strong>${escapeHTML(formatMoney(price, currency))} HT</strong>
          <small>${escapeHTML(formatMoney(Math.round(price * VAT_MULTIPLIER), currency))} TTC</small>
        </span>
      </a>
      <div class="cd13-quick-product__actions">
        <button type="button" class="cd13-quick-add-button" data-cd13-quick-add disabled aria-busy="true">${loadingLabel}</button>
        <span class="cd13-quick-status" data-cd13-quick-status aria-live="polite"></span>
      </div>
      <div class="cd13-quick-panel" data-cd13-quick-panel hidden></div>
    </article>`;
  };

  const renderGroup = (title, items, renderer) => !items?.length ? '' : `
    <section class="cd13-search-group">
      <h3 class="cd13-search-group__title">${escapeHTML(title)}</h3>
      <div class="cd13-search-group__items">${items.map(renderer).join('')}</div>
    </section>`;

  const renderResults = ({ form, query, menuData, predictive }) => {
    const labels = form.dataset;
    const results = predictive?.resources?.results || {};
    const products = prepareProducts(results.products || [], query);
    const menu = menuMatches(menuData, query);
    const collections = results.collections || [];
    const pages = results.pages || [];
    const articles = results.articles || [];
    const queries = results.queries || [];

    if (!(products.length + menu.length + collections.length + pages.length + articles.length + queries.length)) {
      return `<div class="cd13-search-empty">${escapeHTML(labels.labelEmpty || 'Aucun résultat')}</div>`;
    }

    const viewAllUrl = `${getRoot()}search?q=${encodeURIComponent(query)}&type=product,page,article`;

    return `<div class="cd13-search-panel">
      ${renderGroup(labels.labelProducts || 'Produits', products.slice(0, 6), (item) => renderProductItem(item, form, query))}
      ${renderGroup(labels.labelMenu || 'Catégories et navigation', menu, (item) => renderGenericItem(item, 'menu'))}
      ${renderGroup(labels.labelCollections || 'Catégories', collections.slice(0, 5), (item) => renderGenericItem(item, 'collection'))}
      ${renderGroup(labels.labelPages || 'Pages', pages.slice(0, 4), (item) => renderGenericItem(item, 'page'))}
      ${renderGroup(labels.labelArticles || 'Ressources', articles.slice(0, 4), (item) => renderGenericItem(item, 'article'))}
      ${renderGroup(labels.labelSuggestions || 'Suggestions', queries.slice(0, 4), (item) => {
        const text = typeof item === 'string' ? item : item?.text || item?.query || item?.title || '';
        return renderGenericItem({
          title: text,
          url: `${getRoot()}search?q=${encodeURIComponent(text)}&type=product,page,article`
        }, 'query');
      })}
      <a class="cd13-search-view-all" href="${escapeHTML(viewAllUrl)}">${escapeHTML(labels.labelViewAll || 'Voir tous les résultats')} →</a>
    </div>`;
  };

  const buildSuggestUrl = (query) => {
    const effectiveQuery = buildEffectiveQuery(query);
    const parameters = new URLSearchParams({ q: effectiveQuery });
    parameters.set('resources[type]', 'product,collection,page,article,query');
    parameters.set('resources[limit]', '6');
    parameters.set('resources[limit_scope]', 'each');
    parameters.set('resources[options][unavailable_products]', 'last');
    parameters.set('resources[options][fields]', 'title,product_type,variants.title,variants.sku,vendor,tag,body');
    return `${getRoot()}search/suggest.json?${parameters}`;
  };

  const initSearch = (form) => {
    const input = form.querySelector(INPUT_SELECTOR);
    const panel = form.querySelector(RESULTS_SELECTOR);
    const menuData = parseMenuData(form);
    let debounceTimer = null;
    let abortController = null;

    if (!input || !panel) return;

    const hide = () => {
      panel.hidden = true;
      panel.innerHTML = '';
      input.setAttribute('aria-expanded', 'false');
    };

    const show = (html) => {
      panel.innerHTML = html;
      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      window.CD13QuickAdd?.hydrate(panel);
      document.dispatchEvent(new CustomEvent('cd13:quick-add-rendered', { detail: { scope: panel } }));
    };

    const search = async () => {
      const query = input.value.trim();
      if (query.length < MIN_QUERY_LENGTH) {
        hide();
        return;
      }

      abortController?.abort();
      abortController = new AbortController();

      try {
        const response = await fetch(buildSuggestUrl(query), {
          headers: { Accept: 'application/json' },
          signal: abortController.signal
        });
        if (!response.ok) throw new Error(`Search failed: ${response.status}`);

        const predictive = await response.json();
        const products = predictive?.resources?.results?.products || [];
        predictive.resources.results.products = await enrichProducts(products, abortController.signal);
        show(renderResults({ form, query, menuData, predictive }));
      } catch (error) {
        if (error.name !== 'AbortError') {
          show(renderResults({ form, query, menuData, predictive: { resources: { results: {} } } }));
        }
      }
    };

    input.setAttribute('aria-controls', panel.id || 'HeaderSearchResults');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');

    input.addEventListener('input', () => {
      clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(search, DEBOUNCE_MS);
    });

    input.addEventListener('focus', () => {
      if (input.value.trim().length >= MIN_QUERY_LENGTH && panel.innerHTML.trim()) {
        panel.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      }
    });

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') hide();
    });

    document.addEventListener('click', (event) => {
      if (!form.contains(event.target)) hide();
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll(SEARCH_SELECTOR).forEach(initSearch);
  });
})();
